import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken, saveUser } from "@/lib/api";
import { GraduationCap, Lock, Mail, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      const data = res.data;
      if (data.status === "pending") {
        toast.info(data.message || "Awaiting admin approval");
        return;
      }
      if (data.status === "rejected") {
        toast.error(data.message || "Access rejected");
        return;
      }
      setToken(data.token); saveUser(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      nav(data.user.role === "staff" ? "/staff" : "/student");
    } catch (err) {
      const s = err.response?.status;
      if (s === 404) {
        toast.info("New here? Please register first.");
        nav("/register");
      } else if (s === 403) {
        toast.error(err.response.data.detail || "Use admin sign-in");
      } else {
        toast.error(err.response?.data?.detail || "Login failed");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0B1120] text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-black text-xl">TrillionBytex<span className="text-blue-400">edu</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Smart TBX Learning Hub</div>
            </div>
          </Link>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl md:text-5xl font-black leading-none tracking-tight">
            Learn <span className="text-emerald-400">deeply.</span><br />
            Code <span className="text-violet-400">daily.</span><br />
            Ship <span className="text-blue-400">skills.</span>
          </h1>
          <p className="text-slate-400 text-sm max-w-md">Trillion Bytex Education — live classes, brain games, coding challenges, and <span className="text-white font-semibold">02</span>, an AI mentor that never sleeps.</p>
        </div>
        <div className="relative z-10 text-xs text-slate-500 flex flex-wrap items-center gap-4">
          <Link to="/admin/login" data-testid="admin-login-link" className="hover:text-violet-400 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Admin sign-in</Link>
          <Link to="/about" data-testid="about-link" className="hover:text-blue-400">About</Link>
          <Link to="/contact" data-testid="contact-link" className="hover:text-emerald-400">Contact</Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Welcome back</div>
            <h2 className="font-display text-3xl font-black text-slate-900 tracking-tight">Sign in to continue</h2>
            <p className="text-sm text-slate-500 mt-2">Enter your email &amp; password. We&apos;ll auto-detect your role.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.edu"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Password</label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
              </div>
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200">
              {loading ? "Please wait…" : "Sign in"}
            </button>
          </form>
          <div className="mt-5 text-sm text-slate-500 text-center">
            New to TrillionBytexedu?{" "}
            <Link to="/register" data-testid="go-to-register" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
              Create account <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-6 text-center lg:hidden flex items-center justify-center gap-4">
            <Link to="/admin/login" data-testid="admin-login-link-mobile" className="text-xs text-slate-500 hover:text-violet-600 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Admin</Link>
            <Link to="/about" data-testid="about-link-mobile" className="text-xs text-slate-500 hover:text-blue-600">About</Link>
            <Link to="/contact" data-testid="contact-link-mobile" className="text-xs text-slate-500 hover:text-emerald-600">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
